import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { awaitingClassificationKey } from "@/features/closed-loop/hooks";
import { trackRecordKey } from "@/features/closed-loop/track-record/hooks";
import type { AppliedScenario, ApplyScenarioInput } from "@/types/applied-scenario.types";

// O CENÁRIO APLICADO no cliente.
// ⚠️ As duas chaves NÃO casam por prefixo entre si: a lista é `["applied-scenarios", lineId]`
// (plural) e o detalhe é `["applied-scenario", id]` (singular). Invalidar as duas é obrigatório,
// não redundante — é a mesma armadilha da run ativa (`["production","active",lineId]`), que
// também não casa com `["production", lineId]`.

const listKey = (lineId: string) => ["applied-scenarios", lineId] as const;
const detailKey = (id: string) => ["applied-scenario", id] as const;

export function useAppliedScenarios(lineId: string | undefined) {
  return useQuery<AppliedScenario[]>({
    queryKey: listKey(lineId ?? ""),
    queryFn: () => api.get<AppliedScenario[]>(`/api/v1/lines/${lineId}/applied-scenarios`),
    enabled: !!lineId,
  });
}

export function useAppliedScenario(id: string | undefined) {
  return useQuery<AppliedScenario>({
    queryKey: detailKey(id ?? ""),
    queryFn: () => api.get<AppliedScenario>(`/api/v1/applied-scenarios/${id}`),
    enabled: !!id,
  });
}

/** Invalidação comum às três mutações: a lista da linha E o detalhe do compromisso tocado. */
function invalidateBoth(qc: QueryClient, lineId: string, id: string) {
  void qc.invalidateQueries({ queryKey: listKey(lineId) });
  void qc.invalidateQueries({ queryKey: detailKey(id) });
}

export function useApplyScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, ...body }: ApplyScenarioInput) =>
      api.post<AppliedScenario>(`/api/v1/lines/${lineId}/applied-scenarios`, body),
    onSuccess: (applied) => {
      invalidateBoth(qc, applied.lineId, applied.id);
      // A linha muda de estado aos olhos de quem a acompanha (passa a ter compromisso vigente)…
      void qc.invalidateQueries({ queryKey: ["lines", applied.lineId] });
      // …e em lote de SEQUÊNCIA a fila real foi reagendada nesta mesma chamada.
      void qc.invalidateQueries({ queryKey: ["production-orders"] });
    },
    // O diálogo trata o erro no lugar (banner): sobreposição de janela e expediente insuficiente
    // são recusas com SAÍDA — o toast genérico tira o usuário do contexto da decisão.
    meta: { skipErrorToast: true },
  });
}

export function useSettleAppliedScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; lineId: string }) =>
      api.post<AppliedScenario>(`/api/v1/applied-scenarios/${id}/settlement`, {}),
    onSuccess: (_data, { id, lineId }) => {
      invalidateBoth(qc, lineId, id);
      // Spec 077 — apurar é o que CRIA trabalho de classificação: o compromisso entra, neste
      // instante, na fila de `/ciclo-fechado`. Sem invalidar, quem tem a fila aberta noutra aba
      // continua vendo "nenhuma apuração esperando julgamento".
      void qc.invalidateQueries({ queryKey: awaitingClassificationKey });
      // Spec 079 — e a RÉGUA: o compromisso apurado entra no universo dela como "aguardando
      // julgamento". `["closed-loop","track-record"]` não casa por prefixo com a chave acima.
      void qc.invalidateQueries({ queryKey: trackRecordKey });
    },
    // ⚠️ SEM `skipErrorToast`. A recusa da apuração tem de aparecer: o 409 `SETTLEMENT_TOO_EARLY`
    // (carência) e o 409 `ALREADY_SETTLED` (alguém apurou antes, ou o tique automático apurou) são
    // exatamente o que um card desatualizado produz, e opt-out sem superfície de erro própria
    // deixava o botão voltar de "Apurando…" para "Apurar resultado" em silêncio. O `MutationCache`
    // global já traduz por `getFriendlyErrorMessage`; `errorTitle` dá o contexto que faltava.
    meta: { errorTitle: "Não foi possível apurar" },
  });
}

export function useCancelAppliedScenario() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; lineId: string; reason: string }) =>
      api.post<AppliedScenario>(`/api/v1/applied-scenarios/${id}/cancel`, { reason }),
    onSuccess: (_data, { id, lineId }) => invalidateBoth(qc, lineId, id),
  });
}
